const isBrowser = typeof window !== 'undefined'
const LOGIN_SOURCE_KEY = 'login-source-path'
const LOGIN_RETURN_KEY = 'login-return-path'
const OAUTH_RETURN_KEY = 'oauth-return-path'

const storeRawPath = (key, value) => {
  if (!isBrowser) return
  if (value && typeof value === 'string') {
    window.sessionStorage.setItem(key, value)
  } else {
    window.sessionStorage.removeItem(key)
  }
}

const consumeRawPath = (key) => {
  if (!isBrowser) return null
  const raw = window.sessionStorage.getItem(key)
  window.sessionStorage.removeItem(key)
  return typeof raw === 'string' && raw.length ? raw : null
}

const peekRawPath = (key) => {
  if (!isBrowser) return null
  const raw = window.sessionStorage.getItem(key)
  return typeof raw === 'string' && raw.length ? raw : null
}

const buildLocationString = (locationLike) => {
  if (!locationLike) return null
  if (typeof locationLike === 'string') return locationLike
  const pathname = typeof locationLike.pathname === 'string' ? locationLike.pathname : ''
  const search = typeof locationLike.search === 'string' ? locationLike.search : ''
  const hash = typeof locationLike.hash === 'string' ? locationLike.hash : ''
  if (!pathname && !search && !hash) return null
  return `${pathname}${search || ''}${hash || ''}`
}

const isUnsafeScheme = (value) => /^(javascript|data|vbscript):/i.test(value || '')

export const sanitizeRedirectTarget = (raw) => {
  if (!isBrowser) return null
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed || isUnsafeScheme(trimmed)) return null

  try {
    const origin = window.location.origin
    const hasProtocol = /^[a-z][a-z0-9.+-]*:/i.test(trimmed)
    const url = new URL(trimmed, hasProtocol ? undefined : origin)
    if (url.origin !== origin) return null
    if (!url.pathname.startsWith('/')) return null
    const params = url.searchParams
    if (params.get('form') === 'login') params.delete('form')
    const normalizedPath = url.pathname.replace(/\/+$/, '') || '/'
    if ((normalizedPath === '/confirm_email') && !params.get('user')) {
      return null
    }
    const search = params.toString()
    return `${url.pathname}${search ? `?${search}` : ''}${url.hash || ''}` || '/'
  } catch {
    return null
  }
}

export const resolvePostLoginDestination = ({
  search,
  state,
  previousLocation,
  fallbackToCurrent = false,
  includeStoredLogin = true,
  includeStoredOauth = true
} = {}) => {
  if (!isBrowser) return null

  const currentSearch = typeof search === 'string' ? search : window.location.search || ''
  const params = new URLSearchParams(currentSearch)

  const redirectCandidate = sanitizeRedirectTarget(params.get('redirect'))
  if (redirectCandidate) return redirectCandidate

  const stateTarget = sanitizeRedirectTarget(
    (state && (state.data?.from || state.from)) || null
  )
  if (stateTarget) return stateTarget

  if (includeStoredLogin) {
    const storedLogin = sanitizeRedirectTarget(window.sessionStorage.getItem(LOGIN_RETURN_KEY))
    if (storedLogin) return storedLogin
  }

  if (includeStoredOauth) {
    const storedOauth = sanitizeRedirectTarget(window.sessionStorage.getItem(OAUTH_RETURN_KEY))
    if (storedOauth) return storedOauth
  }

  const previousTarget = sanitizeRedirectTarget(buildLocationString(previousLocation))
  if (previousTarget) return previousTarget

  if (fallbackToCurrent) {
    const currentTarget = sanitizeRedirectTarget(
      `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`
    )
    if (currentTarget) return currentTarget
  }

  return null
}

export const storeLoginReturnPath = (value) => {
  if (!isBrowser) return
  const sanitized = sanitizeRedirectTarget(value)
  if (sanitized) window.sessionStorage.setItem(LOGIN_RETURN_KEY, sanitized)
}

export const storeOauthReturnPath = (value) => {
  if (!isBrowser) return
  const sanitized = sanitizeRedirectTarget(value)
  if (sanitized) window.sessionStorage.setItem(OAUTH_RETURN_KEY, sanitized)
}

export const clearStoredDestinations = () => {
  if (!isBrowser) return
  window.sessionStorage.removeItem(LOGIN_RETURN_KEY)
  window.sessionStorage.removeItem(OAUTH_RETURN_KEY)
  window.sessionStorage.removeItem(LOGIN_SOURCE_KEY)
}

export const storeLoginSource = (locationLike) => {
  storeRawPath(LOGIN_SOURCE_KEY, buildLocationString(locationLike))
}

export const consumeLoginSource = () => sanitizeRedirectTarget(consumeRawPath(LOGIN_SOURCE_KEY))

export const peekLoginSource = () => sanitizeRedirectTarget(peekRawPath(LOGIN_SOURCE_KEY))

export const peekLoginReturnPath = () => {
  if (!isBrowser) return null
  return sanitizeRedirectTarget(window.sessionStorage.getItem(LOGIN_RETURN_KEY))
}

export const buildConfirmEmailPath = (userId, redirectTarget) => {
  const params = new URLSearchParams()
  const normalizedUser = typeof userId === 'string' ? userId.trim() : ''
  const normalizedRedirect = typeof redirectTarget === 'string' ? redirectTarget.trim() : ''

  if (normalizedUser) params.set('user', normalizedUser)

  const redirectIsValid = normalizedRedirect && normalizedRedirect.startsWith('/') &&
    !normalizedRedirect.startsWith('/confirm_email') &&
    !normalizedRedirect.includes('/institutional/login')

  if (redirectIsValid) params.set('redirect', normalizedRedirect)

  const query = params.toString()
  return `/confirm_email${query ? `?${query}` : ''}`
}
